import React, { useState, useEffect, useRef, useContext, useReducer } from "react";
import { parseISO, format, isSameDay } from "date-fns";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import { green, grey } from "@material-ui/core/colors";
import CloseIcon from "@material-ui/icons/Close";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DoneIcon from "@material-ui/icons/Done";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import AccessTimeIcon from "@material-ui/icons/AccessTime";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import MarkdownWrapper from "../MarkdownWrapper";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: "400px",
    maxWidth: "600px",
    width: "90vw",
    height: "80vh",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: theme.mode === "light" ? "#607D8B" : "#455A64",
    color: "#fff",
    "& .MuiDialogTitle-root": {
      padding: 0,
      flex: 1,
    },
  },
  headerInfo: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontWeight: 600,
    fontSize: "15px",
    color: "#fff",
    lineHeight: 1.2,
  },
  spyLabel: {
    fontSize: "11px",
    color: "rgba(255,255,255,0.8)",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  closeButton: {
    color: "#fff",
    padding: "6px",
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    backgroundImage:
      theme.mode === "light"
        ? `url(${whatsBackground})`
        : `url(${whatsBackgroundDark})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  dateDivider: {
    textAlign: "center",
    margin: "12px 0",
    "& span": {
      backgroundColor: theme.mode === "light" ? "#e1f3fb" : "#374045",
      color: theme.mode === "light" ? "#303030" : "#d1d7db",
      padding: "4px 12px",
      borderRadius: "8px",
      fontSize: "12px",
      fontWeight: 500,
      boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
    },
  },
  messageBubble: {
    maxWidth: "75%",
    padding: "6px 8px 4px",
    borderRadius: "8px",
    marginBottom: "4px",
    position: "relative",
    wordWrap: "break-word",
    fontSize: "13px",
    lineHeight: 1.4,
    boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
  },
  messageLeft: {
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#202c33",
    color: theme.mode === "light" ? "#303030" : "#e9edef",
    marginRight: "auto",
    borderTopLeftRadius: 0,
  },
  messageRight: {
    backgroundColor: theme.mode === "light" ? "#d9fdd3" : "#005c4b",
    color: theme.mode === "light" ? "#303030" : "#e9edef",
    marginLeft: "auto",
    borderTopRightRadius: 0,
  },
  messageInternal: {
    backgroundColor: theme.mode === "light" ? "#fff9c4" : "#4a4522",
    color: theme.mode === "light" ? "#303030" : "#e9edef",
    marginLeft: "auto",
    borderTopRightRadius: 0,
    fontStyle: "italic",
  },
  messageFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "2px",
    marginTop: "2px",
  },
  messageTime: {
    fontSize: "11px",
    color: theme.mode === "light" ? "#667781" : "#8696a0",
  },
  ackIcon: {
    fontSize: "16px",
    marginLeft: "2px",
  },
  ackSent: {
    color: theme.mode === "light" ? "#667781" : "#8696a0",
  },
  ackReceived: {
    color: theme.mode === "light" ? "#667781" : "#8696a0",
  },
  ackRead: {
    color: "#53bdeb",
  },
  senderName: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#6bcbef",
    marginBottom: "2px",
  },
  mediaPlaceholder: {
    color: theme.mode === "light" ? "#667781" : "#8696a0",
    fontStyle: "italic",
    fontSize: "12px",
  },
  messageImage: {
    maxWidth: "100%",
    maxHeight: "300px",
    borderRadius: "4px",
    marginBottom: "4px",
    cursor: "pointer",
  },
  noMessages: {
    textAlign: "center",
    color: theme.mode === "light" ? "#667781" : "#8696a0",
    marginTop: "40px",
    fontSize: "14px",
  },
  reactionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    marginTop: -4,
    marginLeft: 4,
    marginBottom: 2,
  },
  reactionBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    fontSize: 13,
    padding: "2px 6px",
    borderRadius: 16,
    backgroundColor: theme.mode === "light" ? "#ffffff" : "#1f2c33",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    cursor: "default",
    lineHeight: "18px",
  },
  reactionEmoji: {
    fontSize: 16,
  },
  reactionCount: {
    fontSize: 11,
    color: theme.mode === "light" ? "#555" : "#ccc",
    fontWeight: 500,
  },
}));

const messagesReducer = (state, action) => {
  switch (action.type) {
    case "LOAD_MESSAGES":
      const newMessages = action.payload;
      const existingIds = new Set(state.map((m) => m.id));
      const uniqueNew = newMessages.filter((m) => !existingIds.has(m.id));
      return [...uniqueNew, ...state];
    case "ADD_MESSAGE":
      const exists = state.find((m) => m.id === action.payload.id);
      if (exists) return state;
      return [...state, action.payload];
    case "UPDATE_MESSAGE":
      const msgToUpdate = action.payload;
      const msgIndex = state.findIndex((m) => m.id === msgToUpdate.id);
      if (msgIndex !== -1) {
        const updated = [...state];
        updated[msgIndex] = { ...updated[msgIndex], ...msgToUpdate };
        return updated;
      }
      return state;
    case "RESET":
      return [];
    default:
      return state;
  }
};

const SpyTicketModal = ({ open, onClose, ticket }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [messages, dispatch] = useReducer(messagesReducer, []);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const companyId = user.companyId;

  // Fetch messages via spy endpoint
  useEffect(() => {
    if (!open || !ticket?.uuid) return;

    dispatch({ type: "RESET" });
    setPageNumber(1);
    setLoading(true);

    const fetchTicketSpy = async () => {
      try {
        // Call with spy=true so backend won't mark as read
        await api.get(`/tickets/u/${ticket.uuid}`, {
          params: { spy: "true" },
        });
      } catch (err) {
        // Ignore - just needed to log access without marking read
      }
    };
    fetchTicketSpy();
  }, [open, ticket?.uuid]);

  // Fetch messages
  useEffect(() => {
    if (!open || !ticket?.uuid) return;

    const fetchMessages = async () => {
      try {
        const { data } = await api.get(`/messages/${ticket.uuid}`, {
          params: { pageNumber, spy: "true" },
        });
        dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
        setHasMore(data.hasMore);
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [open, ticket?.uuid, pageNumber]);

  // Scroll to bottom on first load
  useEffect(() => {
    if (!loading && pageNumber === 1 && messages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView();
      }, 100);
    }
  }, [loading, messages.length, pageNumber]);

  // WebSocket - listen for new messages in real-time
  useEffect(() => {
    // Socket pode ser nulo (logout forçado, instante antes de conectar): sem
    // isto o socket.on lançava e derrubava a tela inteira (11-09-2026).
    if (!socket) return undefined;
    if (!open || !ticket?.uuid) return;

    const ticketUuid = ticket.uuid;

    // Join chatbox room (same as MessagesList - needed for reliable delivery)
    const onConnect = () => {
      socket.emit("joinChatBox", `${ticketUuid}`);
    };

    // Join immediately
    socket.emit("joinChatBox", `${ticketUuid}`);

    const ticketNumericId = ticket.id;

    const onAppMessage = (data) => {
      if (data.action === "create" && data.ticket.uuid === ticketUuid) {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }

      if (data.action === "update") {
        const matchByUuid = data?.message?.ticket?.uuid === ticketUuid;
        const matchById = data?.message?.ticketId === ticketNumericId;
        if (matchByUuid || matchById) {
          dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
        }
      }
    };

    socket.on("connect", onConnect);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.emit("joinChatBoxLeave", `${ticketUuid}`);
      socket.off("connect", onConnect);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [open, ticket?.uuid, companyId, socket]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop } = e.currentTarget;
    if (scrollTop < 50) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const renderAck = (message) => {
    if (!message.fromMe) return null;

    if (message.ack === 0 || message.ack === 1) {
      return <AccessTimeIcon className={`${classes.ackIcon} ${classes.ackSent}`} />;
    }
    if (message.ack === 2) {
      return <DoneIcon className={`${classes.ackIcon} ${classes.ackReceived}`} />;
    }
    if (message.ack === 3) {
      return <DoneAllIcon className={`${classes.ackIcon} ${classes.ackReceived}`} />;
    }
    if (message.ack >= 4) {
      return <DoneAllIcon className={`${classes.ackIcon} ${classes.ackRead}`} />;
    }
    return null;
  };

  const renderDateDivider = (message, index) => {
    if (index === 0) return true;
    const prevDate = parseISO(messages[index - 1].createdAt);
    const currDate = parseISO(message.createdAt);
    return !isSameDay(prevDate, currDate);
  };

  const renderMessageBody = (message) => {
    // Image
    if (message.mediaType === "image" && message.mediaUrl) {
      return (
        <>
          <img
            src={message.mediaUrl}
            alt="media"
            className={classes.messageImage}
          />
          {message.body && message.body !== message.mediaUrl && (
            <MarkdownWrapper>{message.body}</MarkdownWrapper>
          )}
        </>
      );
    }

    // Location
    if (message.mediaType === "locationMessage" && message.body.includes("|")) {
      return <span className={classes.mediaPlaceholder}>📍 {i18n.t("ticket.spyMediaLocation")}</span>;
    }

    // Contact (vCard)
    if (message.mediaType === "contactMessage" || (message.body && message.body.includes("BEGIN:VCARD"))) {
      return <span className={classes.mediaPlaceholder}>👤 {i18n.t("ticket.spyMediaContact")}</span>;
    }

    // Audio
    if (message.mediaType === "audio" || message.mediaType === "ptt") {
      return <span className={classes.mediaPlaceholder}>🎵 {i18n.t("ticket.spyMediaAudio")}</span>;
    }

    // Video
    if (message.mediaType === "video") {
      return <span className={classes.mediaPlaceholder}>🎥 {i18n.t("ticket.spyMediaVideo")}</span>;
    }

    // Document
    if (message.mediaType === "document" || message.mediaType === "application") {
      return <span className={classes.mediaPlaceholder}>📄 {i18n.t("ticket.spyMediaDocument")}</span>;
    }

    // Sticker
    if (message.mediaType === "sticker") {
      return <span className={classes.mediaPlaceholder}>🏷️ Sticker</span>;
    }

    // Text
    if (message.body) {
      if (message.body.includes("data:image/png;base64")) {
        return <span className={classes.mediaPlaceholder}>📍 {i18n.t("ticket.spyMediaLocation")}</span>;
      }
      return <MarkdownWrapper>{message.body}</MarkdownWrapper>;
    }

    return null;
  };

  const renderReactions = (message) => {
    if (!message.reactions || typeof message.reactions !== "object") return null;

    const entries = Object.entries(message.reactions);
    if (entries.length === 0) return null;

    return (
      <div className={classes.reactionsContainer}>
        {entries.map(([emoji, senders]) => (
          <span
            key={emoji}
            className={classes.reactionBadge}
            title={Array.isArray(senders) ? senders.join(", ") : ""}
          >
            <span className={classes.reactionEmoji}>{emoji}</span>
            {Array.isArray(senders) && senders.length > 1 && (
              <span className={classes.reactionCount}>{senders.length}</span>
            )}
          </span>
        ))}
      </div>
    );
  };

  const getBubbleClass = (message) => {
    if (message.isPrivate) return `${classes.messageBubble} ${classes.messageInternal}`;
    if (message.fromMe) return `${classes.messageBubble} ${classes.messageRight}`;
    return `${classes.messageBubble} ${classes.messageLeft}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
    >
      {/* Header */}
      <div className={classes.header}>
        <Avatar
          src={ticket?.contact?.urlPicture}
          style={{ width: 40, height: 40 }}
        />
        <div className={classes.headerInfo}>
          <Typography className={classes.contactName} noWrap>
            {ticket?.contact?.name || "---"}
          </Typography>
          <Typography className={classes.spyLabel}>
            <VisibilityIcon style={{ fontSize: 13 }} />
            {i18n.t("ticket.spyBanner")}
          </Typography>
        </div>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      {/* Messages */}
      <div
        className={classes.messagesContainer}
        onScroll={handleScroll}
        ref={containerRef}
      >
        {loading && pageNumber === 1 ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={40} />
          </div>
        ) : messages.length === 0 ? (
          <Typography className={classes.noMessages}>
            {i18n.t("ticket.spyNoMessages")}
          </Typography>
        ) : (
          messages.map((message, index) => (
            <React.Fragment key={message.id}>
              {renderDateDivider(message, index) && (
                <div className={classes.dateDivider}>
                  <span>
                    {format(parseISO(message.createdAt), "dd/MM/yyyy")}
                  </span>
                </div>
              )}
              <div className={getBubbleClass(message)}>
                {!message.fromMe && ticket?.isGroup && message.contact && (
                  <div className={classes.senderName}>
                    {message.contact.name}
                  </div>
                )}
                {renderMessageBody(message)}
                {renderReactions(message)}
                <div className={classes.messageFooter}>
                  <span className={classes.messageTime}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                  {renderAck(message)}
                </div>
              </div>
            </React.Fragment>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
    </Dialog>
  );
};

export default SpyTicketModal;
